import {
    Box,
    Button,
    BoxProps,
    Card,
    CardProps,
    TableContainerProps,
    Typography,
    TypographyProps,
    Chip,
    TextField,
    TextFieldProps,
    useTheme,
    TableSortLabel,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    OutlinedInput,
    MenuItem,
    Checkbox,
    ListItemText,
    FormControlProps,
    Tooltip,
    SvgIcon,
} from "@mui/material";
import {
    GridColumnVisibilityModel,
    GridPaginationModel,
    GridRowParams,
} from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DataTable from "components/formsUI/DataTable";
import { useAuthContext } from "contexts/authContext";
import dayjs, { Dayjs } from "dayjs";
import * as Styles from "pages/employee/users/listUsers/listUsers.styles";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as userService from "services/user-service";
import * as departmentService from "services/department-service";
import { PagedUserDto, PagedUserFilter } from "services/user-service";
import { statusStyleCertificate } from "utilities/colorHelper";
import {
    parseSessionStorage,
    getSessionStorage,
    pasrseLocalStorage,
    getLocalStorage,
} from "utilities/sessionStorage";

const initialColumnVisibility: { [key: string]: boolean } = {
    departement: false,
    stamNummer: false,
    pNummer: false,
    renewDate: false,
};

const ListUsers = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const { checkAuth } = useAuthContext();

    const [isIniting, setIsIniting] = useState<boolean>(true);
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [orderBy, setOrderBy] = useState("startDatum");
    const [loadingsUsers, setLoadingsUsers] = useState<boolean>(false);
    const [users, setUsers] = useState<PagedUserDto[]>([]);
    const [currentPage, setCurrentPage] = React.useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
    const [count, setCount] = React.useState<number>(0);
    const [columns, setColumns] = React.useState<{ [key: string]: boolean }>(
        initialColumnVisibility,
    );

    const [naam, setNaam] = useState<string>("");
    const [stamNummer, setStamNummer] = useState<string>("");
    const [pNummer, setPNummer] = useState<string>("");
    const [organisatie, setOrganisatie] = useState<string>("");
    const [referentie, setReferentie] = useState<string>("");
    const [departement, setDepartement] = useState<string>("");
    const [actief, setActief] = useState<string[]>([]);
    const [startDatum, setStartDatum] = useState<Dayjs | null>(null);
    const [eindDatum, setEindDatum] = useState<Dayjs | null>(null);
    const [status, setStatus] = useState<string[]>([]);
    const [renewDate, setRenewDate] = useState<Dayjs | null>(null);

    const [departements, setDepartements] = useState<string[]>([]);
    const [selectedDepartements, setSelectedDepartements] = useState<string[]>(
        [],
    );

    const [timeouts, setTimeouts] = React.useState<NodeJS.Timeout[]>([]);

    const handleColumnVisibility = (columns: GridColumnVisibilityModel) => {
        pasrseLocalStorage<GridColumnVisibilityModel>("users", columns);
        setColumns(columns);
    };

    const statuses = checkAuth(
        [
            "BeheerAttesten",
            "IntrekkenAttesten",
            "ControleAttesten",
            "OpvolgenAttesten",
            "Geen attest",
        ],
        false,
    )
        ? [
              "Goed te keuren",
              "Geldig",
              "Toekomstig geldig",
              "Verlopen",
              "Ingetrokken",
              "Geen attest",
          ]
        : checkAuth(["External"])
          ? [
                "Goed te keuren",
                "Geldig",
                "Toekomstig geldig",
                "Verlopen",
                "Ingetrokken",
                "Geen attest",
            ]
          : [
                "Goed te keuren",
                "Geldig",
                "Toekomstig geldig",
                "Verlopen",
                "Geen attest",
            ];

    const handleChangeFilter = (
        value: any,
        updateFunction: (value: any) => void,
        filterKey: string,
    ): void => {
        console.log("filter", value, typeof value);
        updateFunction(value);
        const filter: { [key: string]: any } = {};
        filter[filterKey] = value;
        parse(filter);
    };

    const parse = (filter: { [key: string]: any }): void => {
        const obj = {
            referentie: "referentie" in filter ? filter.referentie : referentie,
            naam: "naam" in filter ? filter.naam : naam,
            stamNummer: "stamNummer" in filter ? filter.stamNummer : stamNummer,
            pNummer: "pNummer" in filter ? filter.pNummer : pNummer,
            organisatie:
                "organisatie" in filter ? filter.organisatie : organisatie,
            departement:
                "departement" in filter ? filter.departement : departement,
            actief: "actief" in filter ? filter.actief : actief,
            startDatum:
                "startDatum" in filter
                    ? dayjs(filter.startDatum)
                    : dayjs(startDatum),
            eindDatum:
                "eindDatum" in filter
                    ? dayjs(filter.eindDatum)
                    : dayjs(eindDatum),
            status: "status" in filter ? filter.status : status,
            orderBy: "orderBy" in filter ? filter.orderBy : orderBy,
            order: "order" in filter ? filter.order : order,
            renewDate: "renewDate" in filter ? filter.renewDate : renewDate,
            currentPage:
                "currentPage" in filter ? filter.currentPage : currentPage,
            rowsPerPage:
                "rowsPerPage" in filter ? filter.rowsPerPage : rowsPerPage,
        };
        parseSessionStorage("users", obj);
    };

    const debounce = () => {
        timeouts.forEach((timeout) => {
            clearTimeout(timeout);
            clearInterval(timeout);
        });
        setTimeouts([]);
        const _timeout = setTimeout(() => {
            getAllInfo();
        }, 1000);
        setTimeouts([...timeouts, _timeout]);
    };

    const getAllInfo = () => {
        setLoadingsUsers(true);
        const filter: PagedUserFilter = {
            page: currentPage + 1,
            pageSize: rowsPerPage,
            direction: order,
            sortBy: orderBy,
            naam: naam,
            stamNummer: stamNummer,
            pNummer: pNummer,
            organisatie: organisatie,
            referentie: referentie,
            departement: selectedDepartements.join(","),
            startDatum: startDatum === null ? undefined : startDatum.toDate(),
            eindDatum: eindDatum === null ? undefined : eindDatum.toDate(),
            renewDatum: renewDate === null ? undefined : renewDate.toDate(),
            latestCertificatestatus: status.length > 0 ? status : undefined,
            statussen: status.length > 0 ? status : undefined,
        };

        if (actief.length > 0) {
            filter.actief = actief.map((a) => (a === "true" ? true : false));
            // console.log(filter.actief);
        }

        console.log(filter);

        userService
            .getPaged(filter)
            .then((res) => {
                setCount(res.rowCount);
                setUsers(res.results);
                setLoadingsUsers(false);
            })
            .catch((err) => {
                console.error("Failed to fetch users", err);
            });
    };

    const handleExportClick = (): void => {
        const filter: PagedUserFilter = {
            page: currentPage + 1,
            pageSize: rowsPerPage,
            direction: order,
            sortBy: orderBy,
            naam: naam,
            stamNummer: stamNummer,
            pNummer: pNummer,
            organisatie: organisatie,
            referentie: referentie,
            departement: selectedDepartements.join(","),
            startDatum: startDatum === null ? undefined : startDatum.toDate(),
            eindDatum: eindDatum === null ? undefined : eindDatum.toDate(),
            renewDatum: renewDate === null ? undefined : renewDate.toDate(),
            latestCertificatestatus: status.length > 0 ? status : undefined,
            statussen: status.length > 0 ? status : undefined,
        };

        if (actief.length > 0) {
            filter.actief = actief.map((a) => (a === "true" ? true : false));
            // console.log(filter.actief);
        }

        userService.exportAllUsers(filter).then((resp: Blob) => {
            var fileUrl = URL.createObjectURL(resp);
            const link = document.createElement("a");
            link.href = fileUrl;
            var fileExntension = resp.type.split("/")[1];

            //Convert the extension to the correct format
            if (
                fileExntension ===
                "vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
                fileExntension = "xlsx";
            } else {
                fileExntension = "csv";
            }

            var now = dayjs().format("DD-MM-YYYY").toString();
            console.log(now, fileExntension);
            link.download = `users_${now}.${fileExntension}`;
            link.click();
        });
    };

    const handleCertificateSort = (property: string) => {
        if (orderBy === property) {
            if (order === "asc") {
                setOrder("desc");
                parse({ order: "desc" });
            } else {
                setOrder("asc");
                parse({ order: "asc" });
            }
            setCurrentPage(0);
        } else {
            setOrderBy(property);
            setOrder("desc");
            parse({ order: "desc", orderBy: property });
            setCurrentPage(0);
        }
    };

    const handleReset = (): void => {
        setNaam("");
        setStamNummer("");
        setPNummer("");
        setOrganisatie("");
        setReferentie("");
        setDepartement("");
        setActief([]);
        setStartDatum(null);
        setEindDatum(null);
        setStatus([]);
        setRenewDate(null);
        setOrder("desc");
        setOrderBy("startDatum");
        setCurrentPage(0);
        setRowsPerPage(25);
        parse({
            naam: "",
            stamNummer: "",
            pNummer: "",
            organisatie: "",
            referentie: "",
            departement: "",
            actief: [],
            startDatum: null,
            eindDatum: null,
            status: [],
            renewDate: null,
            order: "desc",
            orderBy: "startDatum",
            currentPage: 0,
            rowsPerPage: 25,
        });
    };

    useEffect(() => {
        setIsIniting(true);
        const savedFilters = getSessionStorage<{
            [key: string]: any;
        }>("users");
        if (savedFilters) {
            if (savedFilters.currentPage) {
                setCurrentPage(savedFilters.currentPage);
            }
            if (savedFilters.rowsPerpage) {
                setRowsPerPage(savedFilters.rowsPerpage);
            }
            if (savedFilters.renewDate) {
                setRenewDate(dayjs(savedFilters.renewDate));
            }
            if (savedFilters.orderBy) {
                setOrderBy(savedFilters.orderBy);
            }
            if (savedFilters.order) {
                setOrder(savedFilters.order);
            }
            if (savedFilters.naam) {
                setNaam(savedFilters.naam);
            }
            if (savedFilters.stamNummer) {
                setStamNummer(savedFilters.stamNummer);
            }
            if (savedFilters.pNummer) {
                setPNummer(savedFilters.pNummer);
            }
            if (savedFilters.organisatie) {
                setOrganisatie(savedFilters.organisatie);
            }
            if (savedFilters.referentie) {
                setReferentie(savedFilters.referentie);
            }
            if (savedFilters.departement) {
                setDepartement(savedFilters.departement);
            }
            if (savedFilters.actief !== undefined) {
                setActief(savedFilters.actief);
            }
            if (savedFilters.startDatum) {
                setStartDatum(dayjs(savedFilters.startDatum));
            }
            if (savedFilters.eindDatum) {
                setEindDatum(dayjs(savedFilters.eindDatum));
            }
            if (savedFilters.status) {
                setStatus(savedFilters.status);
            }
        }
        const headers = getLocalStorage<GridColumnVisibilityModel>("users");
        if (headers) setColumns(headers);
        if (departements.length === 0) {
            departmentService.getDepartmentNames().then((res: string[]) => {
                setDepartements([...res]);
            });
        }
        setIsIniting(false);
    }, []);

    useEffect(() => {
        if (!isIniting) {
            getAllInfo();
        }
    }, [
        isIniting,
        actief,
        currentPage,
        selectedDepartements,
        eindDatum,
        naam,
        organisatie,
        pNummer,
        referentie,
        rowsPerPage,
        stamNummer,
        startDatum,
        status,
        order,
        orderBy,
        renewDate,
    ]);

    const checkKeyEnter = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            debounce();
        }
    };

    const updatePagination = (model: GridPaginationModel) => {
        const { pageSize, page } = model;
        setRowsPerPage(pageSize);
        setCurrentPage(page);
        parse({ currentPage: page, rowsPerPage: pageSize });
    };

    const handleCertClick = (
        event: React.MouseEvent,
        certificateId: string | null,
    ): void => {
        event.stopPropagation();
        navigate(`/beheer/attesten/${certificateId}`);
    };

    const handleRowClick = (params: GridRowParams<PagedUserDto>): void => {
        navigate(`${params.row.id}`);
    };

    const wrapperBoxProps: CardProps = {
        sx: Styles.wrapper(theme),
    };

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme),
    };

    const listCertificatesTitleProps: TypographyProps = {
        variant: "h4",
        sx: Styles.listCertificatesTitle(theme),
    };

    const tableContainerProps: TableContainerProps = {
        sx: Styles.tableContainer(theme),
    };

    const tableSearchProps: BoxProps = {
        sx: Styles.tableSearch(theme),
    };

    const tableTextFieldProps: TextFieldProps = {
        sx: Styles.tableTextField(theme),
        size: "small",
        onKeyDown: checkKeyEnter,
    };

    const statusFilterProps: FormControlProps = {
        size: "small",
        sx: Styles.statusFilter(theme),
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const tableFilterProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Card {...wrapperBoxProps}>
            <Box {...titleWrapperProps}>
                <Typography {...listCertificatesTitleProps}>
                    {t("users.overview")}
                </Typography>
            </Box>
            <Box {...tableContainerProps}>
                <DataTable
                    rows={users}
                    columns={[
                        {
                            field: "naam",
                            headerName: t("users.naam"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        label={t("users.naam")}
                                        value={naam}
                                        onChange={(e) =>
                                            handleChangeFilter(
                                                e.target.value,
                                                setNaam,
                                                "naam",
                                            )
                                        }
                                    />
                                    <TableSortLabel
                                        active={orderBy === "naam"}
                                        direction={
                                            orderBy === "naam" ? order : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("naam")
                                        }
                                    />
                                </Box>
                            ),
                            renderCell: (params) => (
                                <span>
                                    {params.row.voorNaam} {params.row.naam}
                                    {params.row.multipleCertificates ? (
                                        <Tooltip
                                            title={t(
                                                "certificates.renewedTooltip",
                                            )}
                                            arrow
                                        >
                                            <SvgIcon
                                                sx={{
                                                    paddingLeft: "2px",
                                                    fontSize: "16px",
                                                    verticalAlign: "middle",
                                                }}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 640 640"
                                                >
                                                    <path d="M286 368C384.5 368 464.3 447.8 464.3 546.3C464.3 562.7 451 576 434.6 576L78 576C61.6 576 48.3 562.7 48.3 546.3C48.3 447.8 128.1 368 226.6 368L286 368zM585.7 169.9C593.5 159.2 608.5 156.8 619.2 164.6C629.9 172.4 632.3 187.4 624.5 198.1L522.1 338.9C517.9 344.6 511.4 348.3 504.4 348.7C497.4 349.1 490.4 346.5 485.5 341.4L439.1 293.4C429.9 283.9 430.1 268.7 439.7 259.5C449.2 250.3 464.4 250.6 473.6 260.1L500.1 287.5L585.7 169.8zM256.3 312C190 312 136.3 258.3 136.3 192C136.3 125.7 190 72 256.3 72C322.6 72 376.3 125.7 376.3 192C376.3 258.3 322.6 312 256.3 312z" />
                                                </svg>
                                            </SvgIcon>
                                        </Tooltip>
                                    ) : (
                                        ""
                                    )}
                                </span>
                            ),
                        },
                        {
                            field: "pNummer",
                            headerName: t("users.pnummer"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        label={t("users.pnummer")}
                                        value={pNummer}
                                        onChange={(e) =>
                                            handleChangeFilter(
                                                e.target.value,
                                                setPNummer,
                                                "pNummer",
                                            )
                                        }
                                    />
                                    <TableSortLabel
                                        active={orderBy === "pnummer"}
                                        direction={
                                            orderBy === "pnummer"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("pnummer")
                                        }
                                    />
                                </Box>
                            ),
                        },
                        {
                            field: "stamNummer",
                            headerName: t("users.stamnummer"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        label={t("users.stamnummer")}
                                        value={stamNummer}
                                        onChange={(e) =>
                                            handleChangeFilter(
                                                e.target.value,
                                                setStamNummer,
                                                "stamNummer",
                                            )
                                        }
                                    />
                                    <TableSortLabel
                                        active={orderBy === "stamnummer"}
                                        direction={
                                            orderBy === "stamnummer"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("stamnummer")
                                        }
                                    />
                                </Box>
                            ),
                        },
                        {
                            field: "organisatie",
                            headerName: t("users.organisatie"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        label={t("users.organisatie")}
                                        value={organisatie}
                                        onChange={(e) =>
                                            handleChangeFilter(
                                                e.target.value,
                                                setOrganisatie,
                                                "organisatie",
                                            )
                                        }
                                    />
                                    <TableSortLabel
                                        active={orderBy === "organisatie"}
                                        direction={
                                            orderBy === "organisatie"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("organisatie")
                                        }
                                    />
                                </Box>
                            ),
                        },
                        {
                            field: "departement",
                            headerName: t("users.departement"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    {/*<TextField
                                        {...tableTextFieldProps}
                                        label={t("users.departement")}
                                        value={organisatie}
                                        onChange={(e) =>
                                            handleChangeFilter(
                                                e.target.value,
                                                setDepartement,
                                                "departement",
                                            )
                                        }
                                    />*/}
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="department-label">
                                            {t(
                                                "requestsTableHeader.department",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="department-label"
                                            multiple
                                            value={selectedDepartements}
                                            onChange={(
                                                event: SelectChangeEvent<
                                                    string[]
                                                >,
                                            ) => {
                                                handleChangeFilter(
                                                    typeof event.target
                                                        .value === "string"
                                                        ? event.target.value.split(
                                                              ",",
                                                          )
                                                        : event.target.value,
                                                    setSelectedDepartements,
                                                    "departement",
                                                );
                                            }}
                                            renderValue={(selected) =>
                                                selected.join(", ")
                                            }
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "requestsTableHeader.department",
                                                    )}
                                                />
                                            }
                                            MenuProps={tableFilterProps}
                                        >
                                            {departements.map((department) => (
                                                <MenuItem
                                                    key={department}
                                                    value={department}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            selectedDepartements.indexOf(
                                                                department,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={department}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "departement"}
                                        direction={
                                            orderBy === "departement"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("departement")
                                        }
                                    />
                                </Box>
                            ),
                        },
                        {
                            field: "actief",
                            headerName: t("users.actief"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="status-label">
                                            {t(
                                                "certificatesTableHeader.status",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="actief-label"
                                            id="actief"
                                            multiple
                                            value={actief}
                                            onChange={(
                                                event: SelectChangeEvent<
                                                    string[]
                                                >,
                                            ) => {
                                                const value =
                                                    typeof event.target
                                                        .value === "string"
                                                        ? event.target.value
                                                              .split(",")
                                                              .map(
                                                                  (item) =>
                                                                      item ===
                                                                      "true",
                                                              ) // Convert string "true"/"false" to boolean
                                                        : event.target.value;
                                                console.log(
                                                    "actief",
                                                    value,
                                                    typeof value,
                                                );
                                                handleChangeFilter(
                                                    typeof event.target
                                                        .value === "string"
                                                        ? event.target.value.split(
                                                              ",",
                                                          )
                                                        : event.target.value,
                                                    setActief,
                                                    "actief",
                                                );
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "certificatesTableHeader.status",
                                                    )}
                                                />
                                            }
                                            renderValue={(selected: string[]) =>
                                                selected
                                                    .map((v) =>
                                                        v === "true"
                                                            ? "In dienst"
                                                            : "Uit dienst",
                                                    )
                                                    .join(", ")
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                            }}
                                            onBlur={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <MenuItem
                                                selected={true}
                                                value="true"
                                            >
                                                <Checkbox
                                                    checked={
                                                        actief.indexOf("true") >
                                                        -1
                                                    }
                                                />
                                                <ListItemText
                                                    primary={t("In dienst")}
                                                />
                                            </MenuItem>
                                            <MenuItem
                                                selected={false}
                                                value="false"
                                            >
                                                <Checkbox
                                                    checked={
                                                        actief.indexOf(
                                                            "false",
                                                        ) > -1
                                                    }
                                                />
                                                <ListItemText
                                                    primary={t("Uit dienst")}
                                                />
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "actief"}
                                        direction={
                                            orderBy === "actief"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("actief")
                                        }
                                    />
                                </Box>
                            ),
                            renderCell: (params) => (
                                <span>
                                    {params.value ? "In dienst" : "Uit dienst"}
                                </span>
                            ),
                        },
                        {
                            field: "startDatum",
                            headerName: t("users.startdatum"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                    >
                                        <DatePicker
                                            label={t("users.list.startDate")}
                                            value={startDatum}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleChangeFilter(
                                                    newValue,
                                                    setStartDatum,
                                                    "startDatum",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    /*onKeyDown={checkKeyEnter}*/ sx={{
                                                        backgroundColor:
                                                            "white",
                                                        borderRadius: "5px",
                                                    }}
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>
                                    <TableSortLabel
                                        active={orderBy === "startDatum"}
                                        direction={
                                            orderBy === "startDatum"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("startDatum")
                                        }
                                    />
                                </Box>
                            ),
                            valueFormatter: (params) => {
                                return params.value
                                    ? dayjs(params.value).format("DD/MM/YYYY")
                                    : "";
                            },
                        },
                        {
                            field: "eindDatum",
                            headerName: t("users.einddatum"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                    >
                                        <DatePicker
                                            label={t("users.list.endDate")}
                                            value={eindDatum}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleChangeFilter(
                                                    newValue,
                                                    setEindDatum,
                                                    "eindDatum",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    /*onKeyDown={checkKeyEnter}*/ sx={{
                                                        backgroundColor:
                                                            "white",
                                                        borderRadius: "5px",
                                                    }}
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>
                                    <TableSortLabel
                                        active={orderBy === "eindDatum"}
                                        direction={
                                            orderBy === "eindDatum"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("eindDatum")
                                        }
                                    />
                                </Box>
                            ),
                            valueFormatter: (params) => {
                                return params.value
                                    ? dayjs(params.value).format("DD/MM/YYYY")
                                    : "";
                            },
                        },
                        {
                            field: "renewDate",
                            headerName: t("users.renewdate"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                    >
                                        <DatePicker
                                            label={t(
                                                "certificatesTableHeader.renewDate",
                                            )}
                                            value={renewDate}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleChangeFilter(
                                                    newValue,
                                                    setRenewDate,
                                                    "renewDatum",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    /*onKeyDown={checkKeyEnter}*/ sx={{
                                                        backgroundColor:
                                                            "white",
                                                        borderRadius: "5px",
                                                    }}
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>
                                    <TableSortLabel
                                        active={orderBy === "renewDatum"}
                                        direction={
                                            orderBy === "renewDatum"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("renewDatum")
                                        }
                                    />
                                </Box>
                            ),
                            renderCell: (params) => (
                                <span>
                                    {params.row.renewDatum &&
                                        params.row.multipleCertificates &&
                                        dayjs(params.row.renewDatum).format(
                                            "DD/MM/YYYY",
                                        )}
                                </span>
                            ),
                        },
                        {
                            field: "referentie",
                            headerName: t("users.referentie"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        label={t("users.referentie")}
                                        value={referentie}
                                        onChange={(e) =>
                                            handleChangeFilter(
                                                e.target.value,
                                                setReferentie,
                                                "referentie",
                                            )
                                        }
                                    />
                                    <TableSortLabel
                                        active={orderBy === "referentie"}
                                        direction={
                                            orderBy === "referentie"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("referentie")
                                        }
                                    />
                                </Box>
                            ),
                            renderCell: (params) => (
                                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                <a
                                    onClick={(e) =>
                                        handleCertClick(
                                            e,
                                            params.row.latestCertificateId,
                                        )
                                    }
                                    // eslint-disable-next-line no-script-url
                                    href="javascript:void(0);"
                                >
                                    {params.value}
                                </a>
                            ),
                        },
                        {
                            field: "status",
                            headerName: t("users.status"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="status-label">
                                            {t(
                                                "certificatesTableHeader.status",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            multiple
                                            value={status}
                                            onChange={(
                                                event: SelectChangeEvent<
                                                    string[]
                                                >,
                                            ) => {
                                                handleChangeFilter(
                                                    typeof event.target
                                                        .value === "string"
                                                        ? event.target.value.split(
                                                              ",",
                                                          )
                                                        : event.target.value,
                                                    setStatus,
                                                    "status",
                                                );
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "certificatesTableHeader.status",
                                                    )}
                                                />
                                            }
                                            renderValue={(selected) =>
                                                selected
                                                    .map((s) => t(s))
                                                    .join(", ")
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                            }}
                                            onBlur={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {statuses.map((name) => (
                                                <MenuItem
                                                    key={name}
                                                    value={name}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            status.indexOf(
                                                                name,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={t(name)}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "status"}
                                        direction={
                                            orderBy === "status"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("status")
                                        }
                                    />
                                </Box>
                            ),
                            renderCell: (params) => {
                                return (
                                    params.value && (
                                        <Chip
                                            sx={{
                                                marginLeft: "10px",
                                                backgroundColor:
                                                    statusStyleCertificate(
                                                        params.value,
                                                    ),
                                                color: "white",
                                            }}
                                            label={t(params.value)}
                                            size="small"
                                        />
                                    )
                                );
                            },
                        },
                    ]}
                    toolbar={[
                        <Button variant="contained" onClick={handleExportClick}>
                            {t("users.export")}
                        </Button>,
                    ]}
                    rowCount={count}
                    loading={loadingsUsers}
                    columnVisibilityModel={columns}
                    onColumnVisibilityModelChange={handleColumnVisibility}
                    updatePagination={updatePagination}
                    paginationModel={{
                        pageSize: rowsPerPage,
                        page: currentPage,
                    }}
                    initialColumnVisibility={initialColumnVisibility}
                    reset={handleReset}
                    onRowClick={handleRowClick}
                />
            </Box>
        </Card>
    );
};

export default ListUsers;
